/* Original, once-only content entrances. Navigation and cart remain independent. */
(() => {
  if (window.leDetoxMotion) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 767px)');
  const seen = new WeakSet();
  const pending = new Set();
  const selector = '.hero-copy > *, .hero-media, .ritual-background, .product-showcase-heading, .commerce-copy, .editorial-copy, .editorial-media, .text-inner, .ingredient, .founder-editorial-copy, .standard-point, .founder-portrait, .closing .page-width, .newsletter-grid';
  const imageSelector = '.hero-media, .ritual-background, .editorial-media, .founder-portrait';
  let config;
  function show(element, immediately = false) {
    if (immediately) element.classList.add('motion-instant');
    element.classList.remove('motion-pending');
    pending.delete(element);
    observer?.unobserve(element);
    seen.add(element);
  }
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) show(entry.target); });
  }, { threshold: 0.01, rootMargin: '0px 0px 40px 0px' }) : null;
  function readConfig() {
    const data = document.body.dataset;
    const effect = ['fade', 'rise', 'clip', 'none'].includes(data.motionReveal) ? data.motionReveal : 'rise';
    return {
      enabled: !!observer && data.motionEnabled !== 'false' && effect !== 'none' && !reduced.matches && !(mobile.matches && data.motionMobile === 'false'),
      effect,
      images: data.motionImages !== 'false',
      duration: ({ fast: 420, normal: 650, slow: 850 })[data.motionSpeed] || 650
    };
  }
  function effectFor(element) {
    const override = element.closest('[data-section-motion]')?.dataset.sectionMotion;
    return ['none','fade','rise','clip'].includes(override) ? override : (element.matches(imageSelector) ? 'clip' : config.effect);
  }
  function initialize(root = document) {
    config = readConfig();
    document.body.style.setProperty('--motion-duration', `${config.duration}ms`);
    // Clear detached targets and immediately expose anything disabled during an entrance.
    [...pending].forEach(element => {
      if (!element.isConnected || !config.enabled || effectFor(element) === 'none' || (!config.images && element.dataset.motionKind === 'image')) show(element, true);
      else element.dataset.motionEffect = effectFor(element);
    });
    root.querySelectorAll(selector).forEach(element => {
      if (!element.closest('main') || seen.has(element) || pending.has(element)) return;
      const image = element.matches(imageSelector);
      if (!config.enabled || effectFor(element) === 'none' || (image && !config.images)) { seen.add(element); return; }
      element.dataset.motionKind = image ? 'image' : 'text';
      element.dataset.motionEffect = effectFor(element);
      let delay = 0;
      if (!mobile.matches && element.parentElement.matches('.hero-copy')) delay = Math.min([...element.parentElement.children].indexOf(element), 4) * 55;
      if (!mobile.matches && element.matches('.ingredient, .standard-point')) delay = [...element.parentElement.children].indexOf(element) % 3 * 55;
      element.style.setProperty('--motion-delay', `${delay}ms`);
      element.classList.add('motion-target', 'motion-pending');
      pending.add(element);
      observer.observe(element);
    });
    revealTarget(document.activeElement);
  }
  function revealTarget(target) {
    if (!(target instanceof Element) || target === document.body || target === document.documentElement) return;
    [...pending].forEach(element => {
      if (element.contains(target) || target.contains(element)) show(element, true);
    });
    // A keyboard target may be inside an entrance already in progress.
    for (let element = target; element; element = element.parentElement) {
      if (element.classList.contains('motion-target')) show(element, true);
    }
  }
  function revealHash() {
    try { if (location.hash) revealTarget(document.getElementById(decodeURIComponent(location.hash.slice(1)))); } catch { /* Ignore malformed external fragments. */ }
  }
  document.addEventListener('focusin', event => revealTarget(event.target));
  document.addEventListener('transitionend', event => {
    if (event.propertyName === 'opacity' && event.target.classList.contains('motion-target') && !pending.has(event.target)) event.target.classList.add('motion-complete');
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [...pending].forEach(element => { if (event.target.contains(element)) show(element, true); });
  });
  document.addEventListener('shopify:section:select', event => revealTarget(event.target));
  document.addEventListener('shopify:block:select', event => revealTarget(event.target));
  reduced.addEventListener('change', () => initialize());
  mobile.addEventListener('change', () => initialize());
  window.addEventListener('hashchange', revealHash);
  window.addEventListener('pageshow', revealHash);
  // Theme-preview updates may replace global attributes without reloading the document.
  const settingsObserver = new MutationObserver(() => initialize());
  settingsObserver.observe(document.body, { attributes: true, attributeFilter: ['data-motion-enabled', 'data-motion-reveal', 'data-motion-speed', 'data-motion-images', 'data-motion-mobile'] });
  window.leDetoxMotion = { refresh: initialize };
  initialize();
  revealHash();
})();
